import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Clear all persisted auth state. Kept outside the component so the axios
// interceptor can call it without a stale closure over React state.
const clearStoredAuth = () => {
  localStorage.removeItem('affiliate_token');
  localStorage.removeItem('affiliate_refresh_token');
  localStorage.removeItem('affiliate_data');
  localStorage.removeItem('user_type');
};

// De-duplicate concurrent refreshes: when several admin requests 401 at once,
// they all await the same in-flight refresh instead of firing N refresh calls.
let refreshPromise = null;

const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  const storedRefreshToken = localStorage.getItem('affiliate_refresh_token');
  if (!storedRefreshToken) return Promise.reject(new Error('No refresh token'));

  refreshPromise = axios
    .post(`${API_URL}/api/auth/refresh`, { refresh_token: storedRefreshToken })
    .then((resp) => {
      const { token: newToken, refreshToken: newRefreshToken } = resp.data;
      localStorage.setItem('affiliate_token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('affiliate_refresh_token', newRefreshToken);
      }
      return newToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('affiliate_token'));
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(localStorage.getItem('user_type') || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      const storedUser = localStorage.getItem('affiliate_data');
      const storedUserType = localStorage.getItem('user_type');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      if (storedUserType) {
        setUserType(storedUserType);
      }
    }
    setLoading(false);
  }, [token]);

  // Global 401 handling: transparently refresh the admin access token and retry
  // the failed request. If the refresh token is missing/expired, clear the
  // session so the user is redirected to login instead of seeing a misleading
  // empty dashboard. Registered once for the app's lifetime.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const isAuthCall = original?.url?.includes('/api/auth/');
        const canRefresh = !!localStorage.getItem('affiliate_refresh_token');

        if (status === 401 && original && !original._retry && !isAuthCall) {
          original._retry = true;

          if (canRefresh) {
            try {
              const newToken = await refreshAccessToken();
              setToken(newToken);
              original.headers = original.headers || {};
              original.headers['x-access-token'] = newToken;
              original.headers['authorization'] = `Bearer ${newToken}`;
              return axios(original);
            } catch (refreshError) {
              // Refresh failed — fall through to clearing the dead session.
            }
          }

          // Either no refresh token is available (e.g. a session created before
          // this flow existed) or the refresh itself failed. The session is
          // dead, so clear it: ProtectedRoute then redirects to /login instead
          // of leaving the user on a misleading empty dashboard.
          clearStoredAuth();
          setToken(null);
          setUser(null);
          setUserType(null);
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const login = (newToken, userData, type = 'affiliate', refreshToken = null) => {
    localStorage.setItem('affiliate_token', newToken);
    localStorage.setItem('affiliate_data', JSON.stringify(userData));
    localStorage.setItem('user_type', type);
    if (refreshToken) {
      localStorage.setItem('affiliate_refresh_token', refreshToken);
    } else {
      localStorage.removeItem('affiliate_refresh_token');
    }
    setToken(newToken);
    setUser(userData);
    setUserType(type);
  };

  const logout = () => {
    clearStoredAuth();
    setToken(null);
    setUser(null);
    setUserType(null);
  };

  const isAdmin = userType === 'admin';

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ token, user, affiliate: user, userType, isAdmin, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app">
          <Routes>
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            <Route path="/dashboard/*" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;

